import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Sparkles, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { adminAPI } from '../services/api';

// Bulk « Compléter par IA » : one job, polled every 2 s ; only DB-matched vehicles are applied
export const CompatAiJobModal = ({ open, onClose, productIds, onDone }) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) { setJob(null); return; }
    setStarting(true);
    adminAPI.compatAiComplete(productIds).then(setJob).catch(e => { toast.error(e.response?.data?.detail || 'Lancement impossible'); onClose(); }).finally(() => setStarting(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!job || job.status !== 'en cours') return undefined;
    timer.current = setTimeout(() => adminAPI.compatAiJob(job.id).then(setJob).catch(() => {}), 2000);
    return () => clearTimeout(timer.current);
  }, [job]);

  const finished = job?.status === 'terminé';
  const close = () => { onClose(); if (job?.done) onDone?.(); };
  const completed = job?.items?.filter(i => i.status === 'complété').length || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="compat-ai-job-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles size={18} className="text-violet-600" /> Compléter les compatibilités par IA</DialogTitle>
          <DialogDescription>L'IA propose les véhicules compatibles d'après la marque, la référence et les numéros OE de chaque fiche. Seuls les véhicules retrouvés dans la base sont ajoutés (mode ajout, rien n'est supprimé).</DialogDescription>
        </DialogHeader>
        {starting || !job ? <div className="py-6 flex justify-center"><Loader2 size={22} className="animate-spin text-violet-600" /></div> : (
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1"><span data-testid="compat-ai-progress">{job.done}/{job.total} fiche(s) traitée(s) · {completed} complétée(s)</span>{!finished && <Loader2 size={14} className="animate-spin text-violet-600" />}</div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-violet-500 transition-all" style={{ width: `${job.total ? (job.done / job.total) * 100 : 0}%` }} /></div>
            </div>
            <ul className="divide-y border rounded-lg max-h-[45vh] overflow-y-auto">
              {(job.items || []).map(it => (
                <li key={it.product_id} className="px-3 py-2 text-sm flex items-start gap-2" data-testid={`compat-ai-item-${it.product_id}`}>
                  {it.status === 'complété' ? <CheckCircle2 size={15} className="text-green-600 mt-0.5 flex-shrink-0" /> : <AlertTriangle size={15} className="text-amber-500 mt-0.5 flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{it.name || it.product_id} <span className="text-xs text-gray-400 font-mono">{it.ref}</span></p>
                    <p className="text-xs text-gray-600">{it.error || (it.status === 'complété' ? `${it.applied} motorisation(s) ajoutée(s) · ${it.vehicles} véhicule(s) reconnu(s)` : it.status)}{it.unmatched?.length ? ` · non reconnus : ${it.unmatched.join(', ')}` : ''}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        <DialogFooter>
          <Button type="button" onClick={close} variant={finished ? 'default' : 'outline'} data-testid="compat-ai-close">{finished ? 'Fermer' : 'Continuer en arrière-plan'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompatAiJobModal;
